import { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Container,
  Grid,
  TextField,
  Divider,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  AccountCircle,
  Apartment,
  Create,
  Edit,
  FiberPin,
  LockOutlined,
  Password,
  Place,
  TravelExplore,
  UploadFileOutlined,
  VerifiedUser,
  Wc,
} from "@mui/icons-material";
import {
  uploadUserImage,
  getUserInfo,
  changePassword,
} from "../../../ApiService/ApiService";
import { useNavigate } from "react-router-dom";
import CustomDialog from "../../../Components/CustomDialog";

function UpdateProfile({ handleLogout }) {
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("userId");
  const profileImage = sessionStorage.getItem("profileImage");
  const role = sessionStorage.getItem("role");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [changePasswordFormData, setChangePasswordFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  const fetchUserInfo = async () => {
    try {
      const response = await getUserInfo(userId);
      // console.log("RES", response?.data);
      setUserData(response?.data?.data);
    } catch (error) {
      console.log("Error: ", error);
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, [userId]);

  console.log("User", userData);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleSubmit = async () => {
    try {
      if (file && file.size > 300 * 1024) {
        setDialog({
          visible: true,
          title: "Error",
          message: "File size must be less than 300KB.",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadUserImage(formData);
      setDialog({
        visible: true,
        title: "Success",
        message: "Image uploaded sucessfully",
        buttons: [{ name: "OK", onPress: handleDialogClose }],
      });
      sessionStorage.setItem("profileImage", response?.data);
      setFile(null);
      setPreview(null);
      navigate("/dashboard");
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Couldn't upload image",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePassword = (event) => {
    const { name, value } = event.target;
    setChangePasswordFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmitChangePassword = async (event) => {
    event.preventDefault();
    if (
      !changePasswordFormData.newPassword ||
      !changePasswordFormData.confirmPassword
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Please fill all the details",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    if (
      changePasswordFormData.newPassword !==
      changePasswordFormData.confirmPassword
    ) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Passwords didn't match",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
      return;
    }
    setLoading(true);
    try {
      const response = await changePassword({
        password: changePasswordFormData.newPassword,
      });
      // console.log("RES", response?.data);
      if (response) {
        if (response?.data?.status === 200) {
          setDialog({
            visible: true,
            title: "Success",
            message: "Password changed",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
          setChangePasswordFormData({
            newPassword: "",
            confirmPassword: "",
          });
          handleLogout();
          return;
        } else {
          const errorMessage = response?.data?.errorMessage;
          setDialog({
            visible: true,
            title: "Error",
            message: errorMessage,
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Error",
          message: "Invalid response from the server",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Failed to change password!",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    } finally {
      setLoading(false);
    }
  };

  const getUserRole = (role) => {
    switch (role) {
      case "ROLE_SCHOOL_ADMIN":
        return "School Admin";
      case "ROLE_SUPER_ADMIN":
        return "Super Admin";
      case "ROLE_TEACHER":
        return "School Teacher";
      case "ROLE_PARENT":
        return "Parent";
      default:
        return "User";
    }
  };
  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="profile tabs"
        >
          <Tab
            label="Edit Details"
            icon={<Edit fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
          <Tab
            label="Change Password"
            icon={<Password fontSize="small" />}
            iconPosition="start"
            sx={{ textTransform: "unset" }}
          />
        </Tabs>
        {tabValue === 0 && (
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              marginTop: 2,
              marginBottom: 6,
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", sm: "100%", md: "75%", lg: "75%" },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} display="flex" flexDirection="column">
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    marginBottom: 3,
                  }}
                >
                  Profile Details
                </Typography>

                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <AccountCircle fontSize="small" sx={{ marginRight: 0.5 }} />{" "}
                    Name:
                  </b>
                  {userData?.firstName} {userData?.lastName}
                </Typography>
                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <Wc fontSize="small" sx={{ marginRight: 0.5 }} /> Gender:
                  </b>
                  {userData?.gender === 1 ? "Male" : "Female"}
                </Typography>
                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <Place fontSize="small" sx={{ marginRight: 0.5 }} />
                    Address:
                  </b>
                  {`${userData?.address?.street1} ${
                    userData?.address?.street2 ? userData?.address?.street2 : ""
                  }`}
                </Typography>
                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <Apartment fontSize="small" sx={{ marginRight: 0.5 }} />
                    City:
                  </b>
                  {userData?.address?.city}
                </Typography>
                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <TravelExplore fontSize="small" sx={{ marginRight: 0.5 }} />
                    State:
                  </b>{" "}
                  {userData?.address?.state}
                </Typography>
                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <FiberPin fontSize="small" sx={{ marginRight: 0.5 }} />
                    Pin:
                  </b>{" "}
                  {userData?.address?.pin}
                </Typography>

                <Typography
                  variant="body1"
                  display="flex"
                  sx={{ fontSize: 16, marginTop: 1 }}
                >
                  <b
                    style={{
                      letterSpacing: 1,
                      marginRight: "10px",
                      color: "gray",
                      display: "flex",
                    }}
                  >
                    <VerifiedUser fontSize="small" sx={{ marginRight: 0.5 }} />
                    Role:
                  </b>
                  {getUserRole(role)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                sx={{
                  display: { xs: "none", md: "flex" },
                  justifyContent: "center",
                }}
              >
                <Divider
                  orientation="vertical"
                  sx={{
                    height: "100%",
                    width: 5,
                    backgroundColor: "lightgray",
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                {" "}
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Avatar
                    src={preview ? preview : profileImage}
                    alt="Profile"
                    sx={{
                      height: 240,
                      width: 240,
                      border: "1px solid gray",
                      borderRadius: "50%",
                      marginBottom: 1,
                    }}
                  />

                  <IconButton
                    onClick={() => {
                      const fileInput = document.getElementById(
                        "contained-button-file"
                      );
                      fileInput.click();
                    }}
                    sx={{
                      position: "absolute",
                      bottom: -10,
                      left: 100,
                      color: "white",
                      backgroundColor: "#2659c8",
                      borderRadius: "50%",
                      "&:hover": {
                        backgroundColor: "white",
                        border: "1px solid #2659c8",
                        color: "#2659c8",
                      },
                    }}
                  >
                    <Create />
                  </IconButton>
                </Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="contained-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="small"
                  onClick={handleSubmit}
                  disabled={!file}
                  sx={{
                    textTransform: "unset",
                    marginTop: 2,
                    height: 40,
                  }}
                >
                  Upload <UploadFileOutlined sx={{ marginLeft: 1 }} />
                </Button>
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontSize: 12, marginTop: 1 }}
                >
                  Note: File size should be less than 300KB
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}
        {tabValue === 1 && (
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              marginTop: 2,
              marginBottom: 1,
            }}
          >
            <form
              onSubmit={handleSubmitChangePassword}
              style={{ display: "block" }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "700",
                  textAlign: "center",
                }}
              >
                Change Password
              </Typography>

              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                size="small"
                label="New Password"
                name="newPassword"
                type="password"
                value={changePasswordFormData.newPassword}
                onChange={handleChangePassword}
                required
              />
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth
                size="small"
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={changePasswordFormData.confirmPassword}
                onChange={handleChangePassword}
                required
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ textTransform: "unset", mt: 2, height: "50px" }}
                disabled={loading}
              >
                {loading ? "Loading..." : "Change Password"}
                <LockOutlined fontSize="small" sx={{ marginLeft: 1 }} />
              </Button>
            </form>
          </Paper>
        )}
      </Box>
    </>
  );
}

export default UpdateProfile;
